<script setup>
import FacebookSvg from '@/assets/Icons/fb_logo.svg';
import IgLogoSvg from '@/assets/Icons/ig_logo.svg';
import LinChartSvg from '@/assets/Icons/line-chart.svg';
import LinkedInSvg from '@/assets/Icons/linkedin_logo.svg';
import FundeasySymbolAlt from '@/assets/fe-logo-alt.png';
import useCurrentUrl from '@/composables/useCurrentUrl';
import { usePhotoService } from '@/composables/usePhotoService';
import { useSnackbarStore } from '@/stores/snackbarInfo';
import { storeToRefs } from 'pinia';
import { Line } from 'vue-chartjs';
import P2PMediaManagerModal from '~/components/modals/P2PMediaManagerModal.vue';
import useEventDataStore from '../stores/eventDataStore';
import copyToClipBoardUtil from '../utils/copy-to-clipboard';

const props = defineProps(['ticketConfig', 'eventData', 'savedData', 'title']);

const router = useRouter();
const route = useRoute();
const { currentUrl } = useCurrentUrl();

const { eventData: stashedEventData, error: errorLoadingData } = storeToRefs(useEventDataStore());
const snackbar = useSnackbarStore();
const eventDataStore = useEventDataStore();

const { data, error: apiError } = await useEventData(1);
eventDataStore.setEventData(data?.value ?? {}, apiError?.value ?? {});
const eventData = data?.value ?? stashedEventData;

const heroImage = ref();
const isOpen = ref(true);
const searchValue = ref('');
const slideShowRef = ref(null);
const activeSlideIndex = ref(0);
const now = new Date();
const newYear = new Date(eventData?.event_start_date);
const time = newYear - now;
const tab = ref(null);
const newUpdateSubject = ref('');
const newUpdateMessage = ref('');
const visitorFilter = ref('15 Days');

const { processPhoto, fileName, imgObj, imgBase64, fileType } = usePhotoService();
const handleFileUpload = (e) => {
  const file = e.dataTransfer?.files[0] || e.target.files[0];
  processPhoto(file);
};

const copy = async (text) => {
  try {
    await copyToClipBoardUtil(text);
    snackbar.setSnackbar('Participant page URL Successfully Copied!', 'success', 2000);
  } catch (error) {
    snackbar.setSnackbar('Failed to copy Participant page URL', 'error', 2000);
  }
};

const mockData = {
  labels: ['January', 'February', 'March', 'April', 'May', 'June', 'July'],
  datasets: [
    {
      label: 'Total Unique Visitors',
      backgroundColor: '#4cb848',
      borderColor: '#4cb848',
      data: [40, 39, 10, 40, 39, 80, 40],
    },
    {
      label: 'Total Page Views',
      backgroundColor: '#0e6cb3',
      borderColor: '#0e6cb3',
      data: [20, 19, 90, 80, 29, 10, 120],
    },
  ],
};

const options = {
  responsive: true,
  maintainAspectRatio: false,
};

useHead({
  title: `${eventData?.event_title || 'FundEasy'}`,
});
</script>

<template>
  <header>
    <v-container style="padding: 0">
      <v-row no-gutters justify="space-between" align="center">
        <v-col>
          <v-row align="center">
            <v-col sm="auto">
              <img style="height: 61px" :src="FundeasySymbolAlt" alt="FundEasy" />
            </v-col>
            <v-divider
              :vertical="true"
              style="height: 50px; display: inline-flex; margin-top: auto; margin-bottom: auto"
            />
            <v-col sm="auto">
              <img
                src="/peer_to_peer_logo.png"
                alt="Peer To Peer logo"
                style="width: 130px; height: auto"
              />
            </v-col>
            <v-col sm="6" class="d-sm-none d-md-flex">
              <v-text-field
                placeholder="Find A Friend"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="compact"
                id="email"
                v-model="searchValue"
                hide-details
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col class="d-sm-none d-md-flex" v-if="route.params.participantId">
          <v-row>
            <v-col>
              <nav class="text-right FundRaisingMenu d-flex justify-end">
                <a>My Pages</a>
                <a>My Profile</a>
                <a style="font-weight: bold">Log Out</a>
                <img src="../assets/Icons/help.svg" alt="help" />
              </nav>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </header>
  <main>
    <v-container
      class="pb-0 pt-0"
      :style="{
        backgroundColor: 'var(--fundeasy-colors-neutral-300)',
        position: 'relative',
        margin: 0,
        maxWidth: '100vw',
        minHeight: '100%',
        overflow: 'auto',
      }"
    >
      <v-row no-gutters style="height: 100%; width: 100%" justify="center">
        <v-col sm="12" md="10" :style="`position:relative`">
          <main
            class="w-100 d-flex flex-column pt-0"
            style="position: relative; top: 0; padding: 7.6% 9.82%; overflow: auto"
          >
            <v-row v-if="route.name !== 'fundraising-id-event'">
              <v-col class="FundRaisingSubSection">
                <v-row no-gutters>
                  <v-col class="text-center">
                    <h1 style="color: #2ac6ca" class="title-case">{{ eventData?.event_title }}</h1>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col style="min-height: 600px">
                    <v-row no-gutters>
                      <v-col>
                        <vueper-slides
                          fixed-height="600px"
                          class="no-shadow bullets-outside"
                          ref="slideShowRef"
                          @ready="activeSlideIndex = $event?.currentSlide?.index"
                          @slide="activeSlideIndex = $event?.currentSlide?.index"
                          bullets-outside
                        >
                          <vueper-slide>
                            <template #content>1</template>
                          </vueper-slide>
                          <vueper-slide>
                            <template #content>2</template>
                          </vueper-slide>
                        </vueper-slides>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <p class="text-center">Caption: {{ activeSlideIndex }}</p>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col>
                    <v-row>
                      <v-col>
                        <v-row>
                          <v-col><h4>Team</h4></v-col>
                        </v-row>
                        <v-row>
                          <v-col>
                            <img
                              style="width: 100%; height: auto"
                              :src="'https://images.pexels.com/photos/89498/pexels-photo-89498.png?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'"
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row>
                          <v-col><h4>Beneficiary</h4></v-col>
                        </v-row>
                        <v-row>
                          <v-col>
                            <img
                              style="width: 100%; height: auto"
                              :src="'https://images.pexels.com/photos/89498/pexels-photo-89498.png?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'"
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-row>
                          <v-col>
                            <p>Team Name</p>
                            <p class="mt-2">Team Location</p>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col>
                        <v-row>
                          <v-col>
                            <p>Beneficiary Name</p>
                            <p class="mt-2">Beneficiary Location</p>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <h3 style="color: #2ac6ca">Welcome To Our Page</h3>
                      </v-col>
                    </v-row>
                    <v-divider class="mt-2 mb-4" />
                    <v-row>
                      <v-col>
                        <p>
                          Potter ipsum wand elf parchment wingardium. Bean yer butter sticking for.
                          Spleens ollivanders cars wheels grim wizard daisies to. Jinxes fanged
                          bott’s whomping ground. Wheels mimubulus knickerbocker die avada pumpkin.
                          Three avada green eyes cup elf viktor for. 12 ravenclaw flavor parvati
                          maze dungeons roaring telescope. Weekly a shunpike werewolf to squashy
                          headless ipsum hiya fanged. Hand giant potion lies clean beuxbatons.
                          Treats ‘zis boggarts easy bag full-moon grindlewald. Green lady holies.
                        </p>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row justify="space-between">
              <v-col sm="12" md="8">
                <v-row>
                  <v-col class="FundRaisingSubSection border">
                    <v-row>
                      <v-col sm="12" md="9">
                        <v-row>
                          <v-col>
                            <table
                              id="fundraising-details-table"
                              style="
                                box-shadow: 0px 1px 2px -1px #1018281a;
                                box-shadow: 0px 1px 3px 0px #1018281a;
                                padding: 10px;
                                width: 100%;
                              "
                            >
                              <thead>
                                <tr>
                                  <td>My Goal</td>
                                  <td>Amount Raised</td>
                                  <td>Percentage of Goal</td>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>
                                    $1,000
                                    <v-icon
                                      icon="mdi-square-edit-outline"
                                      color="#EAB308"
                                      size="x-small"
                                      style="left: 3px"
                                    ></v-icon>
                                  </td>
                                  <td>$6000</td>
                                  <td>60%</td>
                                </tr>
                              </tbody>
                            </table>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col class="text-center">
                            <vue-countdown :time="time" v-if="time" v-slot="displayTime">
                              <span class="FundraisingTimeBlock">
                                <span class="FundraisingTimeBlockValue">
                                  {{ displayTime?.days }}
                                </span>
                                <span class="FundraisingTimeBlockLabel"> Days </span>
                              </span>
                              <span class="FundraisingTimeBlock">
                                <span class="FundraisingTimeBlockValue">
                                  {{ displayTime?.hours }}
                                </span>
                                <span class="FundraisingTimeBlockLabel"> Hrs </span>
                              </span>
                              <span class="FundraisingTimeBlock">
                                <span class="FundraisingTimeBlockValue">
                                  {{ displayTime?.minutes }}
                                </span>
                                <span class="FundraisingTimeBlockLabel"> Mins </span>
                              </span>
                              <span class="FundraisingTimeBlock">
                                <span class="FundraisingTimeBlockValue">
                                  {{ displayTime?.seconds }}
                                </span>
                                <span class="FundraisingTimeBlockLabel"> Secs </span>
                              </span>
                            </vue-countdown>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col sm="12" md="3" class="d-flex position-relative">
                        <v-row>
                          <v-col class="position-relative d-flex justify-center align-end">
                            <div
                              class="FundraiserProgressElement d-flex align-end justify-center mt-md-0 mt-sm-4"
                            >
                              <v-progress-linear
                                color="#4bb848"
                                model-value="60"
                                rounded
                                height="30"
                                style="
                                  transform: translateX(-50%) rotate(-90deg);
                                  position: absolute;
                                  max-width: 130px;
                                  bottom: 100px;
                                "
                              />
                              <div
                                class="d-flex justify-center align-center"
                                style="
                                  width: 70px;
                                  height: 70px;
                                  background-color: #4bb848;
                                  border-radius: 35px;
                                  z-index: 2;
                                "
                              >
                                <span style="color: #ffffff; font-size: 20px; font-weight: 600"
                                  >60%</span
                                >
                              </div>
                            </div>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="FundRaisingSubSection border">
                    <v-row>
                      <v-col>
                        <h3 style="color: #2ac6ca">Our Latest Update</h3>
                        <v-divider class="mt-2 mb-1" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <div
                          style="
                            border: 1px solid #d4d4d4;
                            background:
                              linear-gradient(0deg, #f5f5f5, #f5f5f5),
                              linear-gradient(0deg, #d4d4d4, #d4d4d4);
                            padding: 15px;
                          "
                          class="mb-5"
                        >
                          <v-row>
                            <v-col>
                              <h3>Latest Update Title</h3>
                              <h5 style="color: #737373; font-weight: 400">
                                Wednesday, December 8, 2023 @ 3:04 PM
                              </h5>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col>
                              <p>
                                Potter ipsum wand elf parchment wingardium. Bean yer butter sticking
                                for. Spleens ollivanders cars wheels grim wizard daisies to. Jinxes
                                fanged bott’s whomping ground. Wheels mimubulus knickerbocker die
                                avada pumpkin. Three avada green eye. (250 max)
                              </p>
                            </v-col>
                          </v-row>
                        </div>
                        <v-row>
                          <v-col>
                            <v-btn
                              elevation="1"
                              class="text-transform-none"
                              append-icon="mdi-chevron-right"
                              href=""
                              variant="outlined"
                            >
                              See More Updates
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row class="mt-10">
                  <v-col>
                    <v-tabs
                      v-model="tab"
                      align-tabs="left"
                      hide-slider
                      selected-class="TabActiveState"
                      class="FundraisingDetailsTabs"
                    >
                      <v-tab :value="1"> Details </v-tab>
                      <v-tab :value="2">
                        Updates<v-avatar class="TabBadge" color="#FFFFFF" size="20">2</v-avatar>
                      </v-tab>
                      <v-tab :value="3"
                        >Donors<v-avatar class="TabBadge" color="#FFFFFF" size="20"
                          >5</v-avatar
                        ></v-tab
                      >
                      <v-tab :value="4" v-if="route.params.participantId"
                        >Our Family<v-avatar class="TabBadge" color="#FFFFFF" size="20"
                          >5</v-avatar
                        ></v-tab
                      >
                      <v-tab :value="5" v-if="route.params.participantId"
                        >My Team<v-avatar class="TabBadge" color="#FFFFFF" size="20"
                          >1</v-avatar
                        ></v-tab
                      >
                      <v-tab :value="6"
                        >Visitors<v-avatar class="TabBadge" color="#FFFFFF" size="20"
                          >20</v-avatar
                        ></v-tab
                      >
                    </v-tabs>
                  </v-col>
                </v-row>
                <v-row class="mt-0">
                  <v-col class="FundRaisingSubSection border mt-0">
                    <v-row>
                      <v-col>
                        <v-window v-model="tab">
                          <v-window-item :value="1" class="FundRaisingSubSectionEventDetails">
                            <v-row>
                              <v-col>
                                <h3 style="color: #2ac6ca">About This Event</h3>
                                <v-divider class="mt-2 mb-1" />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <p>
                                  <iframe
                                    width="560"
                                    height="315"
                                    src="https://www.youtube-nocookie.com/embed/uTNx6wV4qVk?si=V7MqWqiLcs71dmME"
                                    title="YouTube video player"
                                    frameborder="0"
                                    allow="
                                      accelerometer;
                                      autoplay;
                                      clipboard-write;
                                      encrypted-media;
                                      gyroscope;
                                      picture-in-picture;
                                      web-share;
                                    "
                                    allowfullscreen
                                  ></iframe>
                                </p>
                                <h2>Jungle Jam: Supporting Amazon Plant Playdates</h2>
                                <h4>Bringing Nature’s Playgrounds to Life</h4>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <p>
                                  Jungle Jam is an exciting charity event that brings together
                                  nature lovers and philanthropists for a day of fun, learning, and
                                  giving back. The event features a conference with experts in the
                                  field of environmental conservation and sustainability, sharing
                                  their insights and experiences on how we can protect and preserve
                                  the Amazon rainforest. After the conference, attendees will enjoy
                                  a delicious dinner featuring a selection of delectable dishes made
                                  with ingredients sourced from the Amazon region. As the sun sets,
                                  participants will embark on a thrilling jungle tour, where they
                                  can experience the breathtaking beauty of the rainforest up close
                                  and personal. To commemorate the event, each attendee will receive
                                  a vibrant and stylish T-shirt with the Jungle Jam logo, designed
                                  to capture the upbeat casual vibe of the event. The T-shirts are
                                  made from sustainable materials, so participants can proudly wear
                                  them as a symbol of their commitment to environmental
                                  sustainability. Jungle Jam is not just a fun event but a
                                  meaningful one. Proceeds from the event will support Amazon Plant
                                  Playdates, an organization dedicated to providing children in the
                                  Amazon region with opportunities to learn about and connect with
                                  the natural world around them. By attending Jungle Jam,
                                  participants can make a difference and contribute to a worthy
                                  cause while having a blast.
                                </p>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <h3 style="color: #2ac6ca">About The Beneficiary</h3>
                                <v-divider class="mt-2 mb-1" />
                              </v-col>
                            </v-row>
                            <v-row justify="space-between">
                              <v-col
                                sm="12"
                                md="3"
                                style="display: flex; justify-content: center; align-items: center"
                              >
                                <img
                                  src="https://1000logos.net/wp-content/uploads/2021/04/Fedex-logo.png"
                                  class="FundRaisingSubSectionBeneficiaryLogo"
                                />
                              </v-col>
                              <v-col sm="12" md="8">
                                <p>
                                  Potter ipsum wand elf parchment wingardium. Half-giant petrified
                                  doe nose beuxbatons them drills. Horseless emporium basilisk
                                  spectacles sight. Mrs turns cores locomotor spleens easy holly.
                                  Treats dog venom captivity bean gargoyles norwegian eyes gnomes
                                  maze. Wingardium mcgonagall potion polyjuice good trevor for
                                  frisbees letters. Tears start-of-term grayback drills downfall
                                  smile letters. Grim memory doxycide orbs in sticking banges phials
                                  fat love. Minister tonight grim yaxley glasses. Banana owl to
                                  mudbloods red hearing cars no bee. Totalus cabbage years out
                                  cursed 20 sorcerer's. Parseltongue would wronski boy cauldron
                                  goblin hair order find thestral. Tail trevor for armchairs knew
                                  pumpkin elf tonight. Dervish fluffy full tell broken.
                                </p>
                              </v-col>
                            </v-row>
                            <v-row class="FundRaisingSubSectionBeneficiaryDetails">
                              <v-col>
                                <v-row no-gutters>
                                  <v-col>
                                    <h5>Beneficiary</h5>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col class="mt-4">
                                    <p>Fun Company Name</p>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col class="mt-4">
                                    <p>
                                      <a href="#" target="_blank">https://test.com</a>
                                    </p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col>
                                <v-row>
                                  <v-col>
                                    <h5>Address</h5>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col class="mt-4">
                                    <p>555 Yellow Bridge Road, Ste 5</p>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col class="mt-4">
                                    <p>Munchkin Land, OZ 35498</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col>
                                <v-row>
                                  <v-col>
                                    <h5>Contact</h5>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col class="mt-4">
                                    <p>PeabodyINC@gmail.com</p>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col class="mt-4">
                                    <p>(456) 896-6548</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-window-item>
                          <v-window-item :value="2" class="FundRaisingSubSectionEventDetails">
                            <v-row>
                              <v-col>
                                <v-row class="mb-3 mt-2">
                                  <v-col>
                                    <h3 style="color: #2ac6ca">Our Updates</h3>
                                    <v-divider class="mt-2 mb-1" />
                                  </v-col>
                                </v-row>
                                <div class="mb-5 FundRaisingUpdateContainer">
                                  <v-row>
                                    <v-col>
                                      <h3 style="font-weight: 400">Post New Update</h3>
                                      <v-divider class="mt-2 mb-1" />
                                    </v-col>
                                  </v-row>
                                  <v-row class="align-center">
                                    <v-form style="width: 100%">
                                      <v-col>
                                        <v-row justify="space-between">
                                          <v-col
                                            md="3"
                                            sm="12"
                                            class="justify-center align-center d-flex"
                                          >
                                            <v-card
                                              style="
                                                width: 150px;
                                                height: 150px;
                                                position: relative;
                                              "
                                              class="d-flex justify-center align-center"
                                            >
                                              <input
                                                ref="fileInput"
                                                style="
                                                  opacity: 0;
                                                  position: absolute;
                                                  width: 100%;
                                                  height: 100%;
                                                "
                                                id="fileInput"
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                @change="handleFileUpload"
                                              />
                                              <img
                                                v-if="imgBase64"
                                                :src="imgBase64"
                                                style="max-width: 80%; height: auto"
                                              />
                                              <v-icon v-else icon="mdi-image-area" size="x-large" />
                                            </v-card>
                                          </v-col>
                                          <v-col sm="12" md="8">
                                            <v-row>
                                              <v-col>
                                                <label for="new-update-subject"
                                                  >Subject<span style="color: red">*</span></label
                                                >
                                              </v-col>
                                            </v-row>
                                            <v-row class="mt-0">
                                              <v-col>
                                                <v-text-field
                                                  id="new-update-subject"
                                                  v-model="newUpdateSubject"
                                                  density="compact"
                                                  variant="solo-filled"
                                                  placeholder="Subject"
                                                />
                                              </v-col>
                                            </v-row>
                                            <v-row>
                                              <v-col>
                                                <label for="new-update-message">Message</label>
                                              </v-col>
                                            </v-row>
                                            <v-row>
                                              <v-col class="py-0">
                                                <FroalaEditorWrapper
                                                  bind-id="new-update-message"
                                                  :froala-value="newUpdateMessage"
                                                  @update="newUpdateMessage = $event"
                                                />
                                              </v-col>
                                            </v-row>
                                          </v-col>
                                        </v-row>
                                        <v-row>
                                          <v-col>
                                            <v-divider class="mb-2 mt-8" />
                                          </v-col>
                                        </v-row>
                                        <v-row justify="end">
                                          <v-col sm="12" md="3">
                                            <v-btn
                                              elevation="1"
                                              class="text-transform-none"
                                              href=""
                                              variant="elevated"
                                              block
                                              prepend-icon="mdi-check-bold"
                                              type="submit"
                                              style="background: #4cb848; color: #ffffff"
                                            >
                                              Publish
                                            </v-btn>
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                    </v-form>
                                  </v-row>
                                </div>
                                <div class="mb-5 FundRaisingUpdateContainer">
                                  <v-row>
                                    <v-col>
                                      <h3>Latest Update Title</h3>
                                      <h5 style="color: #737373; font-weight: 400">
                                        Wednesday, December 8, 2023 @ 3:04 PM
                                      </h5>
                                    </v-col>
                                  </v-row>
                                  <v-row>
                                    <v-col>
                                      <p>
                                        Potter ipsum wand elf parchment wingardium. Bean yer butter
                                        sticking for. Spleens ollivanders cars wheels grim wizard
                                        daisies to. Jinxes fanged bott’s whomping ground. Wheels
                                        mimubulus knickerbocker die avada pumpkin. Three avada green
                                        eye. (250 max)
                                      </p>
                                    </v-col>
                                  </v-row>
                                  <v-row>
                                    <v-col>
                                      <p style="font-size: 12px; color: #737373">
                                        Share this update:
                                      </p>
                                    </v-col>
                                  </v-row>
                                  <v-row class="mt-0 align-center" justify="space-between">
                                    <v-col sm="12" md="3">
                                      <v-row no-gutters>
                                        <v-col>
                                          <FacebookSvg style="width: 30px; height: 30px" />
                                        </v-col>
                                        <v-col>
                                          <img src="/x-logo.png" alt="X" />
                                        </v-col>
                                        <v-col>
                                          <LinkedInSvg style="width: 30px; height: 30px" />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                    <v-col sm="12" md="2" class="mr-md-4">
                                      <v-row
                                        class="text-center py-2"
                                        style="border: 1px solid #d4d4d4"
                                      >
                                        <v-col class="px-0 py-0">
                                          <v-icon
                                            icon="mdi-square-edit-outline"
                                            color="#EAB308"
                                            size="x-large"
                                          />
                                        </v-col>
                                        <v-col class="px-0 py-0">
                                          <v-icon
                                            icon="mdi-trash-can-outline"
                                            color="#DC2626"
                                            size="x-large"
                                          />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </div>
                                <div class="mb-5 FundRaisingUpdateContainer">
                                  <v-row>
                                    <v-col>
                                      <h3>Latest Update Title</h3>
                                      <h5 style="color: #737373; font-weight: 400">
                                        Wednesday, December 8, 2023 @ 3:04 PM
                                      </h5>
                                    </v-col>
                                  </v-row>
                                  <v-row>
                                    <v-col>
                                      <p>
                                        Potter ipsum wand elf parchment wingardium. Bean yer butter
                                        sticking for. Spleens ollivanders cars wheels grim wizard
                                        daisies to. Jinxes fanged bott’s whomping ground. Wheels
                                        mimubulus knickerbocker die avada pumpkin. Three avada green
                                        eye. (250 max)
                                      </p>
                                    </v-col>
                                  </v-row>
                                  <v-row>
                                    <v-col>
                                      <p style="font-size: 12px; color: #737373">
                                        Share this update:
                                      </p>
                                    </v-col>
                                  </v-row>
                                  <v-row class="mt-0 align-center" justify="space-between">
                                    <v-col sm="12" md="3">
                                      <v-row no-gutters>
                                        <v-col>
                                          <FacebookSvg style="width: 30px; height: 30px" />
                                        </v-col>
                                        <v-col>
                                          <img src="/x-logo.png" alt="X" />
                                        </v-col>
                                        <v-col>
                                          <LinkedInSvg style="width: 30px; height: 30px" />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                    <v-col sm="12" md="2" class="mr-md-4">
                                      <v-row
                                        class="text-center py-2"
                                        style="border: 1px solid #d4d4d4"
                                      >
                                        <v-col class="px-0 py-0">
                                          <v-icon
                                            icon="mdi-square-edit-outline"
                                            color="#EAB308"
                                            size="x-large"
                                          />
                                        </v-col>
                                        <v-col class="px-0 py-0">
                                          <v-icon
                                            icon="mdi-trash-can-outline"
                                            color="#DC2626"
                                            size="x-large"
                                          />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </div>
                                <div class="mb-5 FundRaisingUpdateContainer">
                                  <v-row>
                                    <v-col>
                                      <h3>Latest Update Title</h3>
                                      <h5 style="color: #737373; font-weight: 400">
                                        Wednesday, December 8, 2023 @ 3:04 PM
                                      </h5>
                                    </v-col>
                                  </v-row>
                                  <v-row>
                                    <v-col>
                                      <p>
                                        Potter ipsum wand elf parchment wingardium. Bean yer butter
                                        sticking for. Spleens ollivanders cars wheels grim wizard
                                        daisies to. Jinxes fanged bott’s whomping ground. Wheels
                                        mimubulus knickerbocker die avada pumpkin. Three avada green
                                        eye. (250 max)
                                      </p>
                                    </v-col>
                                  </v-row>
                                  <v-row>
                                    <v-col>
                                      <p style="font-size: 12px; color: #737373">
                                        Share this update:
                                      </p>
                                    </v-col>
                                  </v-row>
                                  <v-row class="mt-0 align-center" justify="space-between">
                                    <v-col sm="12" md="3">
                                      <v-row no-gutters>
                                        <v-col>
                                          <FacebookSvg style="width: 30px; height: 30px" />
                                        </v-col>
                                        <v-col>
                                          <img src="/x-logo.png" alt="X" />
                                        </v-col>
                                        <v-col>
                                          <LinkedInSvg style="width: 30px; height: 30px" />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                    <v-col sm="12" md="2" class="mr-md-4">
                                      <v-row
                                        class="text-center py-2"
                                        style="border: 1px solid #d4d4d4"
                                      >
                                        <v-col class="px-0 py-0">
                                          <v-icon
                                            icon="mdi-square-edit-outline"
                                            color="#EAB308"
                                            size="x-large"
                                          />
                                        </v-col>
                                        <v-col class="px-0 py-0">
                                          <v-icon
                                            icon="mdi-trash-can-outline"
                                            color="#DC2626"
                                            size="x-large"
                                          />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </div>
                              </v-col>
                            </v-row>
                          </v-window-item>
                          <v-window-item :value="3" class="FundRaisingSubSectionEventDetails">
                            <v-row>
                              <v-col class="mb-2 mt-2">
                                <h3 style="color: #2ac6ca">Our Donors</h3>
                                <v-divider class="mt-2" />
                              </v-col>
                            </v-row>
                            <v-row justify="end">
                              <v-col sm="12" md="3">
                                <v-btn
                                  elevation="1"
                                  class="text-transform-none"
                                  href=""
                                  variant="elevated"
                                  block
                                  style="
                                    background: var(--fundeasy-colors-warning-500);
                                    color: #ffffff;
                                  "
                                  prepend-icon="mdi-plus"
                                  size="small"
                                >
                                  Add Donor
                                </v-btn>
                              </v-col>
                              <v-col sm="12" md="3">
                                <v-btn
                                  elevation="1"
                                  class="text-transform-none"
                                  href=""
                                  variant="elevated"
                                  block
                                  style="
                                    background: var(--fundeasy-colors-warning-500);
                                    color: #ffffff;
                                  "
                                  prepend-icon="mdi-printer-pos-outline"
                                  size="small"
                                >
                                  Print Donor List
                                </v-btn>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-divider class="my-1" />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-row class="flex-nowrap flex-sm-row-reverse flex-md-row">
                                  <v-col sm="12" md="auto" class="order-last order-md-first">
                                    <h3>Anonymous Donor</h3>
                                  </v-col>
                                  <v-col sm="12" md="3" class="px-0">
                                    <v-chip
                                      variant="outlined"
                                      color="green"
                                      density="comfortable"
                                      size="small"
                                      >Credit/Debit</v-chip
                                    >
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col class="text-end">
                                <v-row no-gutters>
                                  <v-col>
                                    <h3>$100</h3>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col>
                                    <p class="FundRaisingDetailText">1 min, 56 secs ago</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-divider class="my-2" />
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-row class="flex-nowrap flex-sm-row-reverse flex-md-row">
                                  <v-col sm="12" md="auto" class="order-last order-md-first">
                                    <h3>Anonymous Donor</h3>
                                  </v-col>
                                  <v-col sm="12" md="3" class="px-0">
                                    <v-chip
                                      variant="outlined"
                                      color="green"
                                      density="comfortable"
                                      size="small"
                                      >Credit/Debit</v-chip
                                    >
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col class="text-end">
                                <v-row no-gutters>
                                  <v-col>
                                    <h3>$100</h3>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col>
                                    <p class="FundRaisingDetailText">1 min, 56 secs ago</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-divider class="my-2" />
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-row class="flex-nowrap flex-sm-row-reverse flex-md-row">
                                  <v-col sm="12" md="auto" class="order-last order-md-first">
                                    <h3>Anonymous Donor</h3>
                                  </v-col>
                                  <v-col sm="12" md="3" class="px-0">
                                    <v-chip
                                      variant="outlined"
                                      color="green"
                                      density="comfortable"
                                      size="small"
                                      >Credit/Debit</v-chip
                                    >
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col class="text-end">
                                <v-row no-gutters>
                                  <v-col>
                                    <h3>$100</h3>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col>
                                    <p class="FundRaisingDetailText">1 min, 56 secs ago</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-divider class="my-2" />
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-row justify="end" class="flex-nowrap text-end">
                                  <v-col sm="auto">Credit/Debit</v-col>
                                  <v-col sm="auto">$300.00</v-col>
                                </v-row>
                              </v-col>
                              <v-divider class="my-2" />
                            </v-row>
                            <v-row justify="end">
                              <v-col>
                                <v-row class="flex-nowrap text-end" justify="end">
                                  <v-col sm="auto"><h4>Grand Total</h4></v-col>
                                  <v-col sm="auto"><h3>$300.00</h3></v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-window-item>
                          <v-window-item :value="4" class="FundRaisingSubSectionEventDetails">
                            <v-row>
                              <v-col>
                                <h3 style="color: #2ac6ca">Our Family</h3>
                                <v-divider class="mt-2 mb-1" />
                              </v-col>
                            </v-row>
                          </v-window-item>
                          <v-window-item :value="5" class="FundRaisingSubSectionEventDetails">
                            <v-row>
                              <v-col class="mb-2 mt-2">
                                <h3 style="color: #2ac6ca">My Team</h3>
                                <v-divider class="mt-2" />
                              </v-col>
                            </v-row>
                            <v-row justify="space-between">
                              <v-col sm="12" md="6">
                                <h4>Donnas Donuts</h4>
                              </v-col>
                              <v-col sm="12" md="6">
                                <v-row>
                                  <v-col sm="12" md="6">
                                    <v-btn
                                      elevation="1"
                                      class="text-transform-none"
                                      href=""
                                      variant="outlined"
                                      block
                                      style="border-color: #2ac6ca; color: #2ac6ca"
                                      prepend-icon="mdi-account-group"
                                      size="small"
                                    >
                                      View Team Page
                                    </v-btn>
                                  </v-col>
                                  <v-col sm="12" md="6">
                                    <v-btn
                                      elevation="1"
                                      class="text-transform-none"
                                      href=""
                                      variant="outlined"
                                      block
                                      style="border-color: #dc2626; color: #dc2626"
                                      prepend-icon="mdi-exit-to-app"
                                      size="small"
                                    >
                                      Leave Team
                                    </v-btn>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                            <v-row justify="space-between">
                              <v-col sm="12" md="6">
                                <p>Team Stats</p>
                              </v-col>
                              <v-col sm="12" md="6">
                                <v-row>
                                  <v-col sm="12" md="6">
                                    <v-row class="text-center">
                                      <v-col>
                                        <p class="FundRaisingDetailText">Team Raised</p>
                                      </v-col>
                                    </v-row>
                                    <v-row no-gutters class="text-center">
                                      <v-col>
                                        <p>$900.00</p>
                                      </v-col>
                                    </v-row>
                                  </v-col>
                                  <v-col sm="12" md="6">
                                    <v-row class="text-center">
                                      <v-col>
                                        <p class="FundRaisingDetailText">Team Ranks</p>
                                      </v-col>
                                    </v-row>
                                    <v-row no-gutters class="text-center">
                                      <v-col>
                                        <img src="/first-place.png" style="height: 20px" />
                                      </v-col>
                                    </v-row>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-divider class="my-1" />
                              </v-col>
                            </v-row>
                            <v-row class="mb-3 mt-2">
                              <v-col sm="12" md="3">
                                <v-row>
                                  <v-col>
                                    <img
                                      src="https://images.pexels.com/photos/89498/pexels-photo-89498.png?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                      style="max-width: 100%"
                                    />
                                  </v-col>
                                </v-row>
                                <v-row class="text-center mt-2">
                                  <v-col class="py-0">
                                    <h6>Torres Family</h6>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center">
                                  <v-col>
                                    <p class="FundRaisingDetailText">Goal: $1,000</p>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center" no-gutters>
                                  <v-col>
                                    <p class="FundRaisingDetailText">Raised: $6000</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col sm="12" md="3">
                                <v-row>
                                  <v-col>
                                    <img
                                      src="https://images.pexels.com/photos/89498/pexels-photo-89498.png?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                      style="max-width: 100%"
                                    />
                                  </v-col>
                                </v-row>
                                <v-row class="text-center mt-2">
                                  <v-col class="py-0">
                                    <h6>Torres Family</h6>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center">
                                  <v-col>
                                    <p class="FundRaisingDetailText">Goal: $1,000</p>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center" no-gutters>
                                  <v-col>
                                    <p class="FundRaisingDetailText">Raised: $6000</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col sm="12" md="3">
                                <v-row>
                                  <v-col>
                                    <img
                                      src="https://images.pexels.com/photos/89498/pexels-photo-89498.png?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                      style="max-width: 100%"
                                    />
                                  </v-col>
                                </v-row>
                                <v-row class="text-center mt-2">
                                  <v-col class="py-0">
                                    <h6>Torres Family</h6>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center">
                                  <v-col>
                                    <p class="FundRaisingDetailText">Goal: $1,000</p>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center" no-gutters>
                                  <v-col>
                                    <p class="FundRaisingDetailText">Raised: $6000</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col sm="12" md="3">
                                <v-row>
                                  <v-col>
                                    <img
                                      src="https://images.pexels.com/photos/89498/pexels-photo-89498.png?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                                      style="max-width: 100%"
                                    />
                                  </v-col>
                                </v-row>
                                <v-row class="text-center mt-2">
                                  <v-col class="py-0">
                                    <h6>Torres Family</h6>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center">
                                  <v-col>
                                    <p class="FundRaisingDetailText">Goal: $1,000</p>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center" no-gutters>
                                  <v-col>
                                    <p class="FundRaisingDetailText">Raised: $6000</p>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col sm="12" md="auto">
                                <v-row>
                                  <v-col>
                                    <p class="FundRaisingDetailText">Invite Others To Join</p>
                                  </v-col>
                                </v-row>
                                <v-row class="text-center">
                                  <v-col class="pt-0">
                                    <FacebookSvg style="width: 30px; height: 30px" />
                                  </v-col>
                                  <v-col class="pt-0">
                                    <IgLogoSvg style="width: 30px; height: 30px" />
                                  </v-col>
                                  <v-col class="pt-0">
                                    <img src="/x-logo.png" alt="X" />
                                  </v-col>
                                  <v-col class="pt-0">
                                    <LinkedInSvg style="width: 30px; height: 30px" />
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-window-item>
                          <v-window-item :value="6" class="FundRaisingSubSectionEventDetails">
                            <v-row>
                              <v-col class="mb-2 mt-2">
                                <h3 style="color: #2ac6ca">My Page Visitors</h3>
                                <v-divider class="mt-2" />
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col sm="12" md="6">
                                <v-row class="flex-sm-row-reverse flex-md-row flex-nowrap">
                                  <v-col sm="12" md="auto"><h4>Total Unique Visitors</h4></v-col>
                                  <v-col sm="12" md="3" class="pl-0">
                                    <h4 style="color: #4cb848">
                                      42 <LinChartSvg style="color: #4cb848; width: 31px" />
                                    </h4>
                                  </v-col>
                                </v-row>
                                <v-row class="flex-sm-row-reverse flex-md-row flex-nowrap">
                                  <v-col sm="12" md="auto"><h4>Total Page Views</h4></v-col>
                                  <v-col sm="12" md="3" class="pl-0">
                                    <h4 style="color: #0e6cb3">
                                      116 <LinChartSvg style="color: #0e6cb3; width: 31px" />
                                    </h4>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col>
                                <v-row>
                                  <v-col>
                                    <p>Show Past</p>
                                  </v-col>
                                </v-row>
                                <v-row no-gutters>
                                  <v-col sm="12" md="4">
                                    <v-select
                                      placeholder="Select View"
                                      style="
                                        min-width: 174px;
                                        background-color: var(--fundeasy-colors-generic-00);
                                      "
                                      variant="outlined"
                                      density="compact"
                                      hide-details
                                      v-model="visitorFilter"
                                      prepend-inner-icon="mdi-calendar"
                                      :items="['15 Days', '30 Days', '45 Days']"
                                    />
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <Line :mockData="mockData" :options="options" />
                              </v-col>
                            </v-row>
                          </v-window-item>
                        </v-window>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
              <v-col sm="12" md="4" lg="3">
                <v-row>
                  <v-col class="px-0">
                    <aside class="FundRaisingSidebar mt-md-4">
                      <v-row>
                        <v-col>
                          <v-btn
                            elevation="1"
                            class="text-transform-none"
                            href=""
                            variant="elevated"
                            block
                          >
                            Offline Donation
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-btn
                            elevation="1"
                            class="text-transform-none"
                            href=""
                            variant="elevated"
                            block
                          >
                            Online Donation
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="FundRaisingSubSection">
                          <v-row>
                            <v-col class="text-center">
                              <h5>Spread The Word</h5>
                              <v-divider class="mt-2 mb-4" />
                            </v-col>
                          </v-row>
                          <v-row no-gutters class="text-center">
                            <v-col>
                              <FacebookSvg style="width: 30px; height: 30px" />
                            </v-col>
                            <v-col>
                              <IgLogoSvg style="width: 30px; height: 30px" />
                            </v-col>
                            <v-col>
                              <img src="/x-logo.png" alt="X" />
                            </v-col>
                            <v-col>
                              <LinkedInSvg style="width: 30px; height: 30px" />
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col>
                              <p class="FundRaisingDetailText">Fundraiser Page Link</p>
                            </v-col>
                          </v-row>
                          <v-row no-gutters class="mt-2 mb-1">
                            <v-text-field
                              disabled
                              solo
                              variant="outlined"
                              density="compact"
                              :value="currentUrl ?? ''"
                              prepend-inner-icon="mdi-web"
                              :rounded="false"
                              class="FundRaisingSubSectionShareLink rounded-0"
                            >
                            </v-text-field>
                            <v-btn
                              elevation="1"
                              class="text-transform-none"
                              variant="flat"
                              style="border-radius: 0 4px 4px 0; height: 40px"
                              @click="copy(currentUrl ?? '')"
                              color="#4CB848"
                              >Copy</v-btn
                            >
                          </v-row>
                          <v-row class="mt-0">
                            <v-col>
                              <p>
                                <a href="#" style="text-decoration: none">
                                  <v-icon icon="mdi-bookmark-box-outline" size="x-large" /> Bookmark
                                  this page</a
                                >
                              </p>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="FundRaisingSubSection">
                          <v-row>
                            <v-col class="text-center">
                              <h5>Get Involved</h5>
                              <v-divider class="mt-2 mb-4" />
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col>
                              <p>
                                <a href="#" style="text-decoration: none">
                                  <v-icon icon="mdi-information-outline" size="x-large" /> Event Web
                                  Page</a
                                >
                              </p>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col>
                              <p>
                                <a href="#" style="text-decoration: none">
                                  <v-icon icon="mdi-bookmark-box-outline" size="x-large" /> Find
                                  Another Participant</a
                                >
                              </p>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col>
                              <p>
                                <a href="#" style="text-decoration: none">
                                  <v-icon icon="mdi-bookmark-box-outline" size="x-large" /> Register
                                  as a Participant</a
                                >
                              </p>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="FundRaisingSubSection">
                          <v-row>
                            <v-col class="text-center">
                              <h5>Top Participant</h5>
                              <v-divider class="mt-2 mb-4" />
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col sm="2" class="pr-0 text-center align-center">
                              <img src="/first-place.png" style="width: 20px" />
                            </v-col>
                            <v-col>
                              <p>Torres Family Nichole Acevedo</p>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="FundRaisingSubSection">
                          <v-row>
                            <v-col class="text-center">
                              <h5>Top Teams</h5>
                              <v-divider class="mt-2 mb-4" />
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col sm="2" class="pr-0 text-center align-center">
                              <img src="/first-place.png" style="width: 20px" />
                            </v-col>
                            <v-col>
                              <p>Torres Family Nichole Acevedo</p>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col sm="2" class="pr-0 text-center align-center">
                              <img src="/second-place.png" style="width: 20px" />
                            </v-col>
                            <v-col>
                              <p>Torres Family Nichole Acevedo</p>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col sm="2" class="pr-0 text-center align-center">
                              <img src="/third-place.png" style="width: 20px" />
                            </v-col>
                            <v-col>
                              <p>Torres Family Nichole Acevedo</p>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col class="FundRaisingSubSection">
                          <v-row>
                            <v-col class="text-center">
                              <h5>Top Sponsors</h5>
                              <v-divider class="mt-2 mb-4" />
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col sm="2" class="pr-0 text-center align-center">
                              <img src="/first-place.png" style="width: 20px" />
                            </v-col>
                            <v-col>
                              <p>Torres Family Nichole Acevedo</p>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col sm="2" class="pr-0 text-center align-center">
                              <img src="/second-place.png" style="width: 20px" />
                            </v-col>
                            <v-col>
                              <p>Torres Family Nichole Acevedo</p>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col sm="2" class="pr-0 text-center align-center">
                              <img src="/third-place.png" style="width: 20px" />
                            </v-col>
                            <v-col>
                              <p>Torres Family Nichole Acevedo</p>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <slot name="main-aside" v-if="$slots['main-aside']"></slot>
                    </aside>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </main>
        </v-col>
      </v-row>
    </v-container>
  </main>
  <P2PMediaManagerModal :model-open="isOpen" />
  <!-- <BaseModal
    :modal-open="openAddTeamModal"
    @close="openAddTeamModal = false"
    max-width="700px"
    backgroundHeader="transparent"
  >
    <template v-slot:header>
      <v-row class="align-top" justify="space-between">
        <v-col sm="1">
          <div
            style="
              border-radius: 24px;
              background: var(--fundeasy-colors-primary-100);
              height: 48px;
              width: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
            "
          >
            <v-icon icon="mdi-account-group" color="#4CB848"></v-icon>
          </div>
        </v-col>
        <v-col sm="10">
          <v-row no-gutters justify="align-center" class="text-left">
            <h5 style="font-size: 18px; text-align: left; font-weight: 500" class="mt-2">
              Create A Team
            </h5>
            <p style="font-size: 14px">
              {{
                confirm_new_team
                  ? 'Please confirm your Team Name.'
                  : 'Please enter a Team Name below and determine if you will also be the Team Captain.'
              }}
            </p>
          </v-row>
        </v-col>
        <v-btn elevation="1"
          @click="openAddTeamModal = false"
          icon="mdi-close"
          variant="text"
          :color="eventData?.event_color || 'info'"
          size="small"
        ></v-btn>
      </v-row>
    </template>
    <template v-slot:default>
      <v-row no-gutters class="pa-4">
        <v-col v-if="!confirm_new_team">
          <v-row no-gutters>
            <label for="new-team-name" class="d-block mb-3"
              >Team Name <span style="color: red">*</span></label
            >
          </v-row>
          <v-row no-gutters>
            <v-text-field
              v-model="new_team_name"
              id="new-team-name"
              density="compact"
              variant="outlined"
              hide-details
              :rules="[(val) => (val ? true : 'Required')]"
            />
          </v-row>
          <v-row class="align-center">
            <v-col sm="7">
              <p>Will you also be the Team Captain?</p>
            </v-col>
            <v-col>
              <v-radio-group v-model="captain_selection" hide-details>
                <v-row>
                  <v-col>
                    <v-radio :value="true" label="Yes" hide-details> </v-radio>
                  </v-col>
                  <v-col>
                    <v-radio :value="false" label="No" hide-details> </v-radio>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-8" justify="end">
            <v-btn elevation="1"
              :color="eventData?.event_color || 'info'"
              variant="outlined"
              :disabled="!new_team_name"
              @click="confirm_new_team = true"
              >Continue</v-btn
            >
          </v-row>
        </v-col>
        <v-col v-if="confirm_new_team">
          <v-row>
            <v-col sm="4">
              <p style="font-size: 14px">Team Name</p>
            </v-col>
            <v-col sm="8">
              <p style="font-weight: 600; font-size: 14px">
                {{ new_team_name }}
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="4">
              <p style="font-size: 14px">Team Captain?</p>
            </v-col>
            <v-col sm="8">
              <p style="font-weight: 600; font-size: 14px">
                {{ captain_selection ? 'Yes' : 'No' }}
              </p>
            </v-col>
          </v-row>
          <v-row class="mt-8" justify="end">
            <v-btn elevation="1"
              :color="eventData?.event_color || 'info'"
              variant="outlined"
              :disabled="!new_team_name"
              @click="confirm_new_team = false"
            >
              Back
            </v-btn>
            <v-btn elevation="1"
              :color="eventData?.event_color || 'info'"
              variant="outlined"
              :disabled="!new_team_name"
              @click="openAddTeamModal = true"
            >
              Confirm
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </template>
  </BaseModal> -->
</template>
<style>
header {
  background-color: var(--fundeasy-colors-generic-00);
  border: 1px solid var(--fundeasy-colors-neutral-300);
  padding-bottom: 5px;
  padding-top: 5px;
  height: 77px;
}

.FundRaisingMenu > * {
  margin-right: 20px;
}

.FundRaisingMenu > *:last-child {
  margin-right: 0;
}

.FundRaisingSubSection {
  background-color: var(--fundeasy-colors-generic-00);
  padding: 30px;
  margin-top: 30px;
  box-shadow: 0px 2px 4px -2px #1018281a;
  box-shadow: 0px 4px 6px -1px #1018281a;
}

.FundRaisingSubSection.border {
  border: 4px solid #2ac6ca !important;
  outline: 1px solid #fff;
  outline-offset: -10px;
}

.vueperslides__bullet .default {
  border: 1px solid #2ac6ca;
}

.vueperslides__bullet--active .default {
  border-width: 6px;
}

#fundraising-details-table {
  border: 1px solid #d4d4d4;
  position: relative;
}

#fundraising-details-table td {
  padding-left: 10px;
  padding-right: 10px;
}

#fundraising-details-table tbody td {
  color: #2ac6ca;
  font-size: 24px;
  font-weight: 600;
  position: relative;
}
#fundraising-details-table tbody td::after {
  content: '';
  width: 1px;
  height: 160%;
  right: 9px;
  background-color: #e5e7eb;
  transform: translate(-50%, -41%);
  position: absolute;
}
#fundraising-details-table tbody td:last-child::after {
  content: none;
}

.FundraisingTimeBlock {
  display: inline-flex;
  flex-direction: column;
  margin-right: 10px;
  text-align: center;
}

.FundraisingTimeBlock .FundraisingTimeBlockValue {
  border: 1px solid #e5e5e5;
  padding: 5px;
  min-width: 40px;
  text-align: center;
  display: inline-block;
  margin-bottom: 3px;
}

.FundraisingDetailsTabs .v-tab {
  cursor: pointer;
  padding: 5px;
  text-transform: none;
}

.FundraisingDetailsTabs .TabActiveState {
  background: #2ac6ca;
  color: var(--fundeasy-colors-generic-00);
  font-weight: bold;
  cursor: default;
}

.FundraisingDetailsTabs .TabBadge {
  border-radius: 10px;
  margin-left: 5px;
  width: 24px !important;
}

.FundRaisingSubSection .FundRaisingSubSectionEventDetails h2 {
  margin-top: 20px;
}

.FundRaisingSubSection .FundRaisingSubSectionEventDetails h4 {
  font-weight: 500;
}

.FundRaisingSubSection .FundRaisingSubSectionBeneficiaryLogo {
  width: 100%;
  height: auto;
}
.FundRaisingSubSection .FundRaisingSubSectionBeneficiaryDetails p {
  font-size: 14px;
}

.FundRaisingSubSection #new-update-subject {
  background-color: #ffffff;
}

.FundRaisingSubSection .FundraiserProgressElement {
  position: relative;
  height: 120px;
  width: 100%;
}

.FundRaisingSidebar button {
  background: #2ac6ca;
  color: #ffffff;
}

.FundRaisingUpdateContainer {
  border: 1px solid #d4d4d4;
  background: linear-gradient(0deg, #f5f5f5, #f5f5f5), linear-gradient(0deg, #d4d4d4, #d4d4d4);
  padding: 15px;
}

aside .FundRaisingSubSection h5 {
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 600;
}

aside .FundRaisingSubSection {
  font-size: 12px;
}
aside .FundRaisingSubSection a {
  color: #0e6cb3;
}

aside .FundRaisingSubSection a i {
  margin-right: 5px;
  color: #737373;
}

.FundRaisingSubSectionShareLink .v-field {
  border-radius: 4px 0 0 4px;
}

.FundRaisingDetailText {
  color: #737373;
  font-size: 12px;
}

.backdrop {
  min-height: 400px;
  height: 400px;
  min-width: 100%;
  position: absolute;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  filter: blur(8px);
  -webkit-filter: blur(8px);
  z-index: 0;
  top: 0;
  left: 0;
}
</style>
~/composables/useCurrentUrl
